import * as React from 'react';
import { useDataMode } from './DataModeContext';
import { useAuth } from './AuthContext';
import {
  USE_FAKE_DATA_IN_DEMO,
  DEMO_FEATURED_GROWS,
  DEMO_FEATURED_PRESETS,
  DEMO_FORUM_POSTS,
  DEMO_FORUM_REPLIES,
  FORUM_TOPICS,
  DEMO_USERS,
  canUserPost,
} from '../data/communityDemoData';

const CommunityContext = React.createContext(null);

export function CommunityProvider({ children }) {
  const dataMode = useDataMode();
  const auth = useAuth();

  const isDemo = dataMode?.isDemo ?? true;
  const isAuthenticated = auth?.user != null;

  // State for collective data
  const [collectiveGrows, setCollectiveGrows] = React.useState([]);
  const [collectivePresets, setCollectivePresets] = React.useState([]);

  // State for forum data
  const [forumPosts, setForumPosts] = React.useState([]);
  const [forumTopics] = React.useState(FORUM_TOPICS);
  const [postReplies, setPostReplies] = React.useState({});

  // Loading states
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Check if user can create content
  const canPost = canUserPost(isDemo, isAuthenticated);

  // Determine if we should use fake data
  const useFakeData = isDemo && USE_FAKE_DATA_IN_DEMO;

  // Load initial data
  React.useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (useFakeData) {
          // Use static demo data
          setCollectiveGrows(DEMO_FEATURED_GROWS);
          setCollectivePresets(DEMO_FEATURED_PRESETS);
          setForumPosts(DEMO_FORUM_POSTS);
        } else {
          // TODO: Fetch from database when implemented
          // For now, fall back to demo data
          setCollectiveGrows(DEMO_FEATURED_GROWS);
          setCollectivePresets(DEMO_FEATURED_PRESETS);
          setForumPosts(DEMO_FORUM_POSTS);
        }
      } catch (err) {
        console.error('Error loading community data:', err);
        setError(err.message);
        // Fall back to demo data on error
        setCollectiveGrows(DEMO_FEATURED_GROWS);
        setCollectivePresets(DEMO_FEATURED_PRESETS);
        setForumPosts(DEMO_FORUM_POSTS);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [useFakeData]);

  // Get replies for a specific post
  const getReplies = React.useCallback(async (postId) => {
    // Check if already loaded
    if (postReplies[postId]) {
      return postReplies[postId];
    }

    if (useFakeData) {
      const replies = DEMO_FORUM_REPLIES[postId] || [];
      setPostReplies(prev => ({ ...prev, [postId]: replies }));
      return replies;
    }

    // TODO: Fetch from database
    const replies = DEMO_FORUM_REPLIES[postId] || [];
    setPostReplies(prev => ({ ...prev, [postId]: replies }));
    return replies;
  }, [useFakeData, postReplies]);

  // Get user info by ID
  const getUser = React.useCallback((userId) => {
    if (useFakeData) {
      return DEMO_USERS[userId] || null;
    }
    // TODO: Fetch from database
    return DEMO_USERS[userId] || null;
  }, [useFakeData]);

  // Get post by ID
  const getPost = React.useCallback((postId) => {
    return forumPosts.find(p => p.id === postId) || null;
  }, [forumPosts]);

  // Get posts by topic
  const getPostsByTopic = React.useCallback((topicId) => {
    if (!topicId || topicId === 'all') {
      return forumPosts;
    }
    return forumPosts.filter(p => p.topicId === topicId);
  }, [forumPosts]);

  // Search posts
  const searchPosts = React.useCallback((query, topicId = null) => {
    const lowerQuery = query.toLowerCase();
    let results = forumPosts.filter(p =>
      p.title.toLowerCase().includes(lowerQuery) ||
      p.content.toLowerCase().includes(lowerQuery) ||
      p.tags.some(t => t.toLowerCase().includes(lowerQuery))
    );

    if (topicId && topicId !== 'all') {
      results = results.filter(p => p.topicId === topicId);
    }

    return results;
  }, [forumPosts]);

  // Get trending posts (most views in last week)
  const getTrendingPosts = React.useCallback((limit = 5) => {
    return [...forumPosts]
      .sort((a, b) => b.views - a.views)
      .slice(0, limit);
  }, [forumPosts]);

  // Get recent posts
  const getRecentPosts = React.useCallback((limit = 10) => {
    return [...forumPosts]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, limit);
  }, [forumPosts]);

  // Get pinned posts
  const getPinnedPosts = React.useCallback(() => {
    return forumPosts.filter(p => p.isPinned);
  }, [forumPosts]);

  // Create new post (only for authenticated non-demo users)
  const createPost = React.useCallback(async (postData) => {
    if (!canPost) {
      throw new Error('You must be logged in to create a post');
    }

    // TODO: Save to database
    const newPost = {
      id: `post-${Date.now()}`,
      ...postData,
      author: auth.user?.displayName || 'Anonymous',
      authorId: auth.user?.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      views: 0,
      likes: 0,
      replyCount: 0,
      isPinned: false,
      isSolved: false,
    };

    setForumPosts(prev => [newPost, ...prev]);
    return newPost;
  }, [canPost, auth]);

  // Create reply (only for authenticated non-demo users)
  const createReply = React.useCallback(async (postId, content) => {
    if (!canPost) {
      throw new Error('You must be logged in to reply');
    }

    // TODO: Save to database
    const newReply = {
      id: `reply-${Date.now()}`,
      postId,
      content,
      author: auth.user?.displayName || 'Anonymous',
      authorId: auth.user?.uid,
      createdAt: new Date().toISOString(),
      likes: 0,
      isAcceptedAnswer: false,
    };

    setPostReplies(prev => ({
      ...prev,
      [postId]: [...(prev[postId] || []), newReply],
    }));

    // Update reply count
    setForumPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, replyCount: p.replyCount + 1 } : p
    ));

    return newReply;
  }, [canPost, auth]);

  // Filter grows
  const filterGrows = React.useCallback((filters = {}) => {
    let results = [...collectiveGrows];

    if (filters.plantType && filters.plantType !== 'all') {
      results = results.filter(g => g.plantType === filters.plantType);
    }
    if (filters.medium && filters.medium !== 'all') {
      results = results.filter(g => g.medium === filters.medium);
    }
    if (filters.search) {
      const query = filters.search.toLowerCase();
      results = results.filter(g =>
        g.name.toLowerCase().includes(query) ||
        g.author.toLowerCase().includes(query) ||
        g.tags.some(t => t.toLowerCase().includes(query))
      );
    }

    return results;
  }, [collectiveGrows]);

  // Filter presets
  const filterPresets = React.useCallback((filters = {}) => {
    let results = [...collectivePresets];

    if (filters.plantType && filters.plantType !== 'all') {
      results = results.filter(p => p.plantType === filters.plantType || p.plantType === 'all');
    }
    if (filters.stage && filters.stage !== 'all') {
      results = results.filter(p => p.stage === filters.stage);
    }
    if (filters.search) {
      const query = filters.search.toLowerCase();
      results = results.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.author.toLowerCase().includes(query)
      );
    }

    return results;
  }, [collectivePresets]);

  const value = {
    // Data
    collectiveGrows,
    collectivePresets,
    forumPosts,
    forumTopics,

    // State
    loading,
    error,
    canPost,
    isDemo,
    useFakeData,

    // Collective methods
    filterGrows,
    filterPresets,

    // Forum methods
    getPost,
    getReplies,
    getUser,
    getPostsByTopic,
    searchPosts,
    getTrendingPosts,
    getRecentPosts,
    getPinnedPosts,
    createPost,
    createReply,
  };

  return (
    <CommunityContext.Provider value={value}>
      {children}
    </CommunityContext.Provider>
  );
}

export function useCommunity() {
  const context = React.useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
}

export default CommunityContext;
